import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import WorkOrdersPage from "./pages/WorkOrdersPage";
import WorkOrderDetailPage from "./pages/WorkOrderDetailPage";
import CreateWorkOrderPage from "./pages/CreateWorkOrderPage";
import BulletinsPage from "./pages/BulletinsPage";
import LessonsPage from "./pages/LessonsPage";
import AdminPage from "./pages/AdminPage";
import ArchivePage from "./pages/ArchivePage";
import ChatPage from "./pages/ChatPage";
import WorkflowDashboardPage from "./pages/WorkflowDashboardPage";
import DownloadsPage from "./pages/DownloadsPage";
import SupervisorReviewPage from "./pages/SupervisorReviewPage";
import SchedulePage from "./pages/SchedulePage";
import CrewRequestQueuePage from "./pages/CrewRequestQueuePage";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/work-orders" element={
        <ProtectedRoute>
          <WorkOrdersPage />
        </ProtectedRoute>
      } />
      
      <Route path="/work-orders/new" element={
        <ProtectedRoute>
          <CreateWorkOrderPage />
        </ProtectedRoute>
      } />
      
      <Route path="/work-orders/:id" element={
        <ProtectedRoute>
          <WorkOrderDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/bulletins" element={
        <ProtectedRoute>
          <BulletinsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/archive" element={
        <ProtectedRoute>
          <ArchivePage />
        </ProtectedRoute>
      } />
      
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      
      <Route path="/lessons" element={
        <ProtectedRoute>
          <LessonsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      
      <Route path="/workflow" element={
        <ProtectedRoute>
          <WorkflowDashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/supervisor-review" element={
        <ProtectedRoute>
          <SupervisorReviewPage />
        </ProtectedRoute>
      } />
      
      <Route path="/schedule" element={
        <ProtectedRoute>
          <SchedulePage />
        </ProtectedRoute>
      } />
      
      <Route path="/crew-requests" element={
        <ProtectedRoute>
          <CrewRequestQueuePage />
        </ProtectedRoute>
      } />
      
      <Route path="/downloads" element={
        <ProtectedRoute>
          <DownloadsPage />
        </ProtectedRoute>
      } />
      
      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#0F172A',
              color: 'white',
              border: 'none'
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
